import type { ActiveRandomEvent, RandomEventEngineState } from "./models/randomEvent.ts";
import { createEmptyRandomEventState } from "./models/randomEvent.ts";
import type { GamePhase } from "./models/phase.ts";
import { isGamePhase } from "./models/phase.ts";
import type {
  AutoNotificationGate,
  NotificationState,
  WorldNotification,
} from "./models/notifications.ts";
import {
  AUTO_NOTIFICATION_GATES,
  LOCALIZED_NOTIFICATION_KINDS,
  createEmptyNotificationState,
} from "./models/notifications.ts";
import type { Season } from "./models/season.ts";
import { isSeason } from "./models/season.ts";
import { DEFAULT_TOTEM_ID, isTotemId } from "./models/totem.ts";
import type {
  ClanState,
  RngState,
  TileCoordinates,
  TileData,
  TileKey,
  Weather,
  WorldState,
} from "./models/worldState.ts";
import {
  isTileTerrain,
  isTileVisibility,
  isWeather,
  WORLD_STATE_SCHEMA_VERSION,
} from "./models/worldState.ts";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isValidTileCoordinates(value: unknown): boolean {
  if (!isRecord(value)) {
    return false;
  }

  return isFiniteNumber(value.q) && isFiniteNumber(value.r);
}

function isValidClan(value: unknown): boolean {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.clanName === "string" &&
    isFiniteNumber(value.population) &&
    isFiniteNumber(value.foodSupplies) &&
    isValidTileCoordinates(value.currentTile) &&
    isFiniteNumber(value.foodBankStreak) &&
    isFiniteNumber(value.famineStreak) &&
    isWeather(value.ambientWeather) &&
    typeof value.movedThisTurn === "boolean"
  );
}

function isValidRng(value: unknown): boolean {
  if (!isRecord(value)) {
    return false;
  }

  return isFiniteNumber(value.seed) && isFiniteNumber(value.state);
}

function isValidTile(value: unknown): value is TileData {
  if (!isRecord(value)) {
    return false;
  }

  return (
    isFiniteNumber(value.q) &&
    isFiniteNumber(value.r) &&
    isTileTerrain(value.terrain) &&
    isTileVisibility(value.visibility) &&
    typeof value.isStartingTile === "boolean" &&
    isFiniteNumber(value.foodDepletion) &&
    value.foodDepletion >= 0 &&
    value.foodDepletion <= 1
  );
}

const LOCALIZED_KINDS = new Set<string>(LOCALIZED_NOTIFICATION_KINDS);
/** Retired kinds still accepted on load, then stripped. */
const LEGACY_LOCALIZED_KINDS = new Set<string>(["tutorialFog"]);
const ACCEPTED_LOCALIZED_KINDS = new Set<string>([
  ...LOCALIZED_KINDS,
  ...LEGACY_LOCALIZED_KINDS,
]);

function isValidNotificationMessage(value: unknown): boolean {
  return (
    isRecord(value) &&
    typeof value.title === "string" &&
    typeof value.body === "string"
  );
}

function isValidNotificationItem(value: unknown): value is WorldNotification {
  if (!isRecord(value)) {
    return false;
  }

  const hasLocalizedKind =
    typeof value.localizedKind === "string" &&
    ACCEPTED_LOCALIZED_KINDS.has(value.localizedKind);
  const hasLocalizedKinds =
    Array.isArray(value.localizedKinds) &&
    value.localizedKinds.length > 0 &&
    value.localizedKinds.every(
      (kind) => typeof kind === "string" && ACCEPTED_LOCALIZED_KINDS.has(kind),
    );
  const hasMessages =
    Array.isArray(value.messages) &&
    value.messages.length > 0 &&
    value.messages.every(isValidNotificationMessage);

  const sourceCount = [hasLocalizedKind, hasLocalizedKinds, hasMessages].filter(
    Boolean,
  ).length;

  return (
    typeof value.id === "string" &&
    sourceCount === 1 &&
    isFiniteNumber(value.messageIndex) &&
    typeof value.requiresDecision === "boolean"
  );
}

const AUTO_GATES = new Set<string>(AUTO_NOTIFICATION_GATES);
const LEGACY_AUTO_GATES = new Set<string>(["tutorialFog"]);
const ACCEPTED_AUTO_GATES = new Set<string>([...AUTO_GATES, ...LEGACY_AUTO_GATES]);

function isValidAutoEmitted(value: unknown): value is readonly AutoNotificationGate[] {
  if (value === undefined) {
    return true;
  }
  if (!Array.isArray(value)) {
    return false;
  }
  return value.every(
    (entry) => typeof entry === "string" && ACCEPTED_AUTO_GATES.has(entry),
  );
}

function isValidNotifications(value: unknown): value is NotificationState {
  if (!isRecord(value)) {
    return false;
  }

  if (
    !Array.isArray(value.items) ||
    !isFiniteNumber(value.nextId) ||
    !isFiniteNumber(value.selectedIndex) ||
    !isValidAutoEmitted(value.autoEmitted)
  ) {
    return false;
  }

  return value.items.every(isValidNotificationItem);
}

function isValidActiveRandomEvent(value: unknown): value is ActiveRandomEvent {
  if (!isRecord(value)) {
    return false;
  }

  if (typeof value.id !== "string" || !isFiniteNumber(value.startedAtTurn)) {
    return false;
  }

  if (value.tile !== undefined && !isValidTileCoordinates(value.tile)) {
    return false;
  }

  if (value.turnsRemaining !== undefined && !isFiniteNumber(value.turnsRemaining)) {
    return false;
  }

  if (
    value.tileEnteredEmitted !== undefined &&
    typeof value.tileEnteredEmitted !== "boolean"
  ) {
    return false;
  }

  return true;
}

function isValidRandomEvent(value: unknown): value is RandomEventEngineState {
  if (!isRecord(value)) {
    return false;
  }

  return value.activeEvent === null || isValidActiveRandomEvent(value.activeEvent);
}

function isValidTileKey(key: string, tile: TileData): boolean {
  return key === `${tile.q},${tile.r}`;
}

function isValidTiles(value: unknown): value is Record<TileKey, TileData> {
  if (!isRecord(value)) {
    return false;
  }

  for (const [key, tile] of Object.entries(value)) {
    if (!isValidTile(tile) || !isValidTileKey(key, tile)) {
      return false;
    }
  }

  return true;
}

function isValidWorldStateShape(value: unknown): boolean {
  if (!isRecord(value)) {
    return false;
  }

  return (
    value.schemaVersion === WORLD_STATE_SCHEMA_VERSION &&
    isGamePhase(value.phase) &&
    isFiniteNumber(value.turn) &&
    isSeason(value.season) &&
    isValidClan(value.clan) &&
    isValidTiles(value.tiles) &&
    isValidRng(value.rng) &&
    (value.notifications === undefined || isValidNotifications(value.notifications)) &&
    (value.randomEvent === undefined || isValidRandomEvent(value.randomEvent))
  );
}

function normalizeTileCoordinates(raw: Record<string, unknown>): TileCoordinates {
  return {
    q: raw.q as number,
    r: raw.r as number,
  };
}

function normalizeTile(raw: Record<string, unknown>): TileData {
  return {
    q: raw.q as number,
    r: raw.r as number,
    terrain: raw.terrain as TileData["terrain"],
    visibility: raw.visibility as TileData["visibility"],
    isStartingTile: raw.isStartingTile as boolean,
    foodDepletion: raw.foodDepletion as number,
  };
}

function normalizeClan(raw: Record<string, unknown>): ClanState {
  return {
    clanName: raw.clanName as string,
    totemId: isTotemId(raw.totemId) ? raw.totemId : DEFAULT_TOTEM_ID,
    population: raw.population as number,
    foodSupplies: raw.foodSupplies as number,
    currentTile: normalizeTileCoordinates(raw.currentTile as Record<string, unknown>),
    foodBankStreak: raw.foodBankStreak as number,
    famineStreak: raw.famineStreak as number,
    ambientWeather: raw.ambientWeather as Weather,
    movedThisTurn: raw.movedThisTurn as boolean,
  };
}

function normalizeRng(raw: Record<string, unknown>): RngState {
  return {
    seed: raw.seed as number,
    state: raw.state as number,
  };
}

function normalizeTiles(raw: Record<string, unknown>): Record<TileKey, TileData> {
  const tiles: Record<TileKey, TileData> = {};

  for (const [key, tile] of Object.entries(raw)) {
    tiles[key as TileKey] = normalizeTile(tile as Record<string, unknown>);
  }

  return tiles;
}

function normalizeNotificationItem(raw: Record<string, unknown>): WorldNotification {
  const item: WorldNotification = {
    id: raw.id as string,
    messageIndex: raw.messageIndex as number,
    requiresDecision: raw.requiresDecision as boolean,
  };
  if (raw.localizedKind !== undefined) {
    item.localizedKind = raw.localizedKind as WorldNotification["localizedKind"];
  }
  if (raw.localizedKinds !== undefined) {
    item.localizedKinds = raw.localizedKinds as WorldNotification["localizedKinds"];
  }
  if (raw.messages !== undefined) {
    item.messages = raw.messages as WorldNotification["messages"];
  }
  return item;
}

function stripLegacyNotificationItem(
  item: WorldNotification,
): WorldNotification | null {
  if (item.localizedKind && !LOCALIZED_KINDS.has(item.localizedKind)) {
    return null;
  }
  if (item.localizedKinds) {
    const kinds = item.localizedKinds.filter((kind) => LOCALIZED_KINDS.has(kind));
    if (kinds.length === 0) {
      return null;
    }
    return { ...item, localizedKinds: kinds };
  }
  return item;
}

function normalizeNotifications(raw: Record<string, unknown>): NotificationState {
  const items = (raw.items as Record<string, unknown>[])
    .map(normalizeNotificationItem)
    .map(stripLegacyNotificationItem)
    .filter((item): item is WorldNotification => item !== null);
  const autoEmitted = (
    Array.isArray(raw.autoEmitted) ? (raw.autoEmitted as string[]) : []
  ).filter((gate): gate is AutoNotificationGate => AUTO_GATES.has(gate));
  const selectedIndex =
    items.length === 0
      ? 0
      : Math.min(Math.max(0, raw.selectedIndex as number), items.length - 1);
  return {
    items,
    selectedIndex,
    nextId: raw.nextId as number,
    autoEmitted,
  };
}

function normalizeActiveRandomEvent(raw: Record<string, unknown>): ActiveRandomEvent {
  const active: ActiveRandomEvent = {
    id: raw.id as string,
    startedAtTurn: raw.startedAtTurn as number,
  };
  if (raw.tile !== undefined) {
    active.tile = normalizeTileCoordinates(raw.tile as Record<string, unknown>);
  }
  if (raw.turnsRemaining !== undefined) {
    active.turnsRemaining = raw.turnsRemaining as number;
  }
  if (raw.tileEnteredEmitted !== undefined) {
    active.tileEnteredEmitted = raw.tileEnteredEmitted as boolean;
  }
  return active;
}

function normalizeRandomEvent(raw: Record<string, unknown>): RandomEventEngineState {
  return {
    activeEvent:
      raw.activeEvent === null
        ? null
        : normalizeActiveRandomEvent(raw.activeEvent as Record<string, unknown>),
  };
}

function normalizeWorldState(raw: Record<string, unknown>): WorldState {
  return {
    schemaVersion: WORLD_STATE_SCHEMA_VERSION,
    phase: raw.phase as GamePhase,
    turn: raw.turn as number,
    season: raw.season as Season,
    clan: normalizeClan(raw.clan as Record<string, unknown>),
    tiles: normalizeTiles(raw.tiles as Record<string, unknown>),
    rng: normalizeRng(raw.rng as Record<string, unknown>),
    notifications:
      raw.notifications === undefined
        ? createEmptyNotificationState()
        : normalizeNotifications(raw.notifications as Record<string, unknown>),
    randomEvent:
      raw.randomEvent === undefined
        ? createEmptyRandomEventState()
        : normalizeRandomEvent(raw.randomEvent as Record<string, unknown>),
  };
}

export function deserializeWorldState(raw: unknown): WorldState | null {
  if (!isValidWorldStateShape(raw) || !isRecord(raw)) {
    return null;
  }

  return normalizeWorldState(raw);
}
